/**
 * Brain Config Service
 *
 * API service for fetching brain-owned configurations via the platform-api proxy.
 * Brain configs include MESH weights, scoring parameters, and CoPilot Guide configs.
 */

import { TonnieClient } from '../client/base';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

/**
 * All brain config types available via the API.
 */
export type BrainConfigType =
  // CoPilot Guide
  | 'checkup_assessment'
  | 'teleportal_setup'
  | 'qultureup_assessment'
  | 'journey_builder'
  // MESH weights
  | 'evaluation_pillars'
  | 'cultural_risk_archetypes'
  | 'skills_thermometer'
  | 'iqulture'
  | 'motivational_questions'
  // Scoring parameters
  | 'cefr_base_scores'
  | 'scoring_anchors'
  | 'skills_blend_config'
  | 'motivation_weights'
  | 'english_weights'
  | 'soft_skills_config'
  | 'diagnosis_risk_thresholds'
  | 'deterministic_cra_rules'
  | 'cra_blend_weights'
  | 'motivational_score_thresholds'
  // Reasoning algorithm
  | 'prompt_templates'
  | 'matching_weights'
  | 'commitment_weights';

export interface BrainConfigResponse {
  success: boolean;
  data: {
    configType: string;
    version: string;
    source?: 'db' | 'cache' | 'static';
    content: Record<string, unknown>;
  };
}

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

export class BrainConfigService {
  constructor(private client: TonnieClient) {}

  /**
   * Get a brain config by type.
   * Calls GET /api/config/brain/:configType
   */
  async getConfig(configType: BrainConfigType, market?: string): Promise<Record<string, unknown>> {
    const params = market ? `?market=${encodeURIComponent(market)}` : '';
    const response = await this.client.get<BrainConfigResponse>(
      `/api/config/brain/${configType}${params}`
    );
    return response.data?.content ?? (response as unknown as Record<string, unknown>);
  }
}

/**
 * Factory function to create a BrainConfigService instance.
 */
export function createBrainConfigService(client: TonnieClient): BrainConfigService {
  return new BrainConfigService(client);
}
